import React from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarEvent } from '../types';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Clock, BookOpen, AlertCircle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { vi } from 'date-fns/locale';

interface EventListProps {
    events: CalendarEvent[];
    currentDate: Date;
}

export const EventList: React.FC<EventListProps> = ({ events, currentDate }) => {
    const { t, i18n } = useTranslation('common');

    // Filter events for current month and sort by date
    const monthlyEvents = events.filter(event => {
        const eventDate = parseISO(event.date);
        return eventDate.getMonth() === currentDate.getMonth() &&
            eventDate.getFullYear() === currentDate.getFullYear();
    }).sort((a, b) => a.date.localeCompare(b.date));

    const getIcon = (type: string) => {
        switch (type) {
            case 'deadline': return <AlertCircle size={14} className="text-red-500" />;
            case 'class': return <BookOpen size={14} className="text-indigo-500" />;
            default: return <Clock size={14} className="text-green-500" />;
        }
    };

    return (
        <Card className="h-full border border-gray-200 shadow-sm bg-white">
            <CardHeader className="bg-gray-50/50 pb-4 border-b">
                <CardTitle className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    {t('calendar.sidebar.upcoming')}
                    <Badge variant="secondary" className="ml-auto bg-white border shadow-sm">
                        {monthlyEvents.length}
                    </Badge>
                </CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-y-auto max-h-[calc(100vh-250px)]">
                {monthlyEvents.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">
                        {t('calendar.sidebar.noEvents')}
                    </div>
                ) : (
                    <div className="divide-y">
                        {monthlyEvents.map(event => (
                            <div key={event.id} className="p-4 hover:bg-slate-50 transition-colors group">
                                <div className="flex gap-3">
                                    <div className="flex flex-col items-center min-w-[3.5rem] pt-1">
                                        <div className="text-xs font-bold text-gray-500 uppercase">
                                            {format(parseISO(event.date), 'MMM', { locale: i18n.language === 'vi' ? vi : undefined })}
                                        </div>
                                        <div className="text-2xl font-bold text-gray-800 leading-none">
                                            {format(parseISO(event.date), 'dd')}
                                        </div>
                                    </div>
                                    <div className="flex-1 space-y-1">
                                        <h4 className="font-semibold text-gray-900 line-clamp-2 leading-tight">
                                            {event.title}
                                        </h4>
                                        <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                                            {getIcon(event.type)}
                                            <span>{event.time}</span>
                                            <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                            <span className="capitalize">{t(`calendar.types.${event.type}`)}</span>
                                        </div>
                                        {event.course && (
                                            <div className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded inline-block mt-1">
                                                {event.course}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
